import { useCallback, useState } from "react";
import { Share2, Check, Copy, Linkedin, Mail, MessageCircle, Twitter } from "lucide-react";

const TITLE = "SACCOs protect against loss. They rarely test for it in advance.";
const TEXT = "Clinq gives SACCO credit teams a repayment-capacity check on every loan application — in under an hour — without replacing the guarantor or collateral model.";

export const saccoShareUrl = () => `${window.location.origin}/api/public/share/saccos`;

const enc = encodeURIComponent;
const NETWORKS = [
  { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: (u) => `https://wa.me/?text=${enc(`${TITLE}\n\n${TEXT}\n\n${u}`)}` },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin, href: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${enc(u)}` },
  { id: "x", label: "X", Icon: Twitter, href: (u) => `https://twitter.com/intent/tweet?text=${enc(TITLE)}&url=${enc(u)}` },
  { id: "email", label: "Email", Icon: Mail, href: (u) => `mailto:?subject=${enc("Worth a look: " + TITLE)}&body=${enc(`${TEXT}\n\n${u}`)}` },
];

export const useSaccoShare = () => {
  const [status, setStatus] = useState("idle");
  const share = useCallback(async () => {
    const url = saccoShareUrl();
    // Link + text only: attaching the image makes WhatsApp/Android drop the URL.
    // The OG tags on the share URL render the thumbnail card on the receiving side.
    const data = { title: TITLE, text: `${TEXT}\n\n${url}`, url };
    if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
      try { await navigator.share(data); setStatus("shared"); } catch { /* dismissed */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2200);
    } catch {
      window.prompt("Copy the link:", url);
    }
  }, []);
  return { status, share };
};

export const SaccoShareButton = ({ variant = "dark", className = "", testid = "sacco-share-btn" }) => {
  const { status, share } = useSaccoShare();
  const base = variant === "dark"
    ? "border-white/25 bg-white/10 text-white hover:bg-white/20"
    : "border-neutral-300 bg-white text-[#0B2A28] hover:border-[#0A8B85] hover:text-[#0A8B85]";
  return (
    <button type="button" onClick={share} data-testid={testid} aria-label="Share this page"
      className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold backdrop-blur-sm transition-[background-color,border-color,color] ${base} ${className}`}>
      {status === "copied" ? <><Check size={16} className="text-[#7BD3CE]" /> Link copied</>
        : status === "shared" ? <><Check size={16} className="text-[#7BD3CE]" /> Shared</>
        : <><Share2 size={16} /> Share this page</>}
    </button>
  );
};

export const SaccoShareNetworks = ({ dark = false }) => {
  const url = saccoShareUrl();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="sacco-share-networks">
      {NETWORKS.map(({ id, label, Icon, href }) => (
        <a key={id} href={href(url)} target={id === "email" ? undefined : "_blank"} rel="noreferrer noopener" aria-label={`Share on ${label}`}
          data-testid={`sacco-share-${id}`}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-[background-color,border-color,color] ${dark
            ? "border-white/20 text-white/80 hover:border-[#E3B84C] hover:text-[#E3B84C]"
            : "border-neutral-300 text-neutral-700 hover:border-[#0A8B85] hover:text-[#0A8B85]"}`}>
          <Icon size={14} /> {label}
        </a>
      ))}
    </div>
  );
};
